import React, { useEffect, useState, useCallback, createContext, useContext } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "@/components/common/UI";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { Pagination, usePagination } from "@/components/common/Pagination";

/** True when the current user can add/edit/delete master records.
 *  Users with role 'user' get read-only access. */
const MastersAuthCtx = createContext(true);
const useCanManage = () => useContext(MastersAuthCtx);

const TABS = [
  { key: "departments", label: "Departments" },
  { key: "doctors", label: "Doctors" },
  { key: "wards", label: "Wards & Beds" },
  { key: "charges", label: "Charges" },
  { key: "medicines", label: "Medicines" },
];

export default function MastersPage() {
  const { user } = useAuth();
  const canManage = !!user && (user.role === "admin" || user.role === "superuser");
  const [tab, setTab] = useState("departments");
  return (
    <MastersAuthCtx.Provider value={canManage}>
    <div data-testid="masters-page">
      <PageHeader eyebrow="Hospital profile" title="Masters"
        subtitle={canManage
          ? "Configure your hospital's doctors, departments, wards & beds, charges and medicines. These appear as dropdowns across the workspace."
          : "Reference lists used across the workspace. Contact your admin to add or change entries."} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[var(--hms-paper)] rounded-sm border border-[var(--hms-border-2)]">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`masters-tab-${t.key}`} className="rounded-sm data-[state=active]:bg-white">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="departments"><DepartmentsTab /></TabsContent>
        <TabsContent value="doctors"><DoctorsTab /></TabsContent>
        <TabsContent value="wards"><WardsTab /></TabsContent>
        <TabsContent value="charges"><ChargesTab /></TabsContent>
        <TabsContent value="medicines"><MedicinesTab /></TabsContent>
      </Tabs>
    </div>
    </MastersAuthCtx.Provider>
  );
}

/* ============== DEPARTMENTS ============== */
const DEPT_BLANK = { name: "", code: "", head: "", notes: "" };
function DepartmentsTab() {
  const canManage = useCanManage();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null=closed, "new"=create, {id,...}=edit
  const [form, setForm] = useState(DEPT_BLANK);
  const load = useCallback(() => api.get("/departments").then((r) => setList(r.data || [])), []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(DEPT_BLANK); setEditing("new"); };
  const openEdit = (row) => { setForm({ name: row.name || "", code: row.code || "", head: row.head || "", notes: row.notes || "" }); setEditing(row); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/departments", form);
      else await api.patch(`/departments/${editing.id}`, form);
      toast.success(editing === "new" ? "Department added" : "Department updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete department?")) return;
    try { await api.delete(`/departments/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-3 space-y-3">
      <div className="flex justify-end">
        {canManage && (
          <Button onClick={openCreate} data-testid="dept-add" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
            <Plus size={14} weight="bold" className="mr-1.5" /> New department
          </Button>
        )}
      </div>
      {list.length === 0 ? <EmptyState title="No departments yet" hint="Add your first department." /> : (
        <SimpleTable
          rows={list}
          columns={[
            { key: "name", label: "Name", className: "font-medium" },
            { key: "code", label: "Code", mono: true },
            { key: "head", label: "HOD" },
            { key: "notes", label: "Notes" },
          ]}
          onEdit={openEdit}
          onDelete={del}
          testidPrefix="dept-row"
        />
      )}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "New department" : `Edit · ${editing?.name}`}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="dept-form">
            <F label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="dept-name" colSpan={2} />
            <F label="Code" value={form.code} onChange={(v) => setForm({ ...form, code: v })} testid="dept-code" />
            <F label="HOD" value={form.head} onChange={(v) => setForm({ ...form, head: v })} testid="dept-head" />
            <FT label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="dept-notes" />
            <DialogFooter className="col-span-2"><Button type="submit" data-testid="dept-submit" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">{editing === "new" ? "Save" : "Update"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============== DOCTORS ============== */
const DOC_BLANK = { name: "", qualification: "", specialization: "", department: "", phone: "", email: "", registration_no: "", consultation_fee: 0, notes: "" };
function DoctorsTab() {
  const canManage = useCanManage();
  const [list, setList] = useState([]);
  const [depts, setDepts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DOC_BLANK);

  const load = useCallback(() => api.get("/doctors").then((r) => setList(r.data || [])), []);
  useEffect(() => { load(); api.get("/departments").then((r) => setDepts(r.data || [])); }, [load]);

  const openCreate = () => { setForm(DOC_BLANK); setEditing("new"); };
  const openEdit = (row) => {
    setForm({
      name: row.name || "", qualification: row.qualification || "", specialization: row.specialization || "",
      department: row.department || "", phone: row.phone || "", email: row.email || "",
      registration_no: row.registration_no || "", consultation_fee: row.consultation_fee || 0, notes: row.notes || "",
    });
    setEditing(row);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, consultation_fee: Number(form.consultation_fee || 0) };
      if (editing === "new") await api.post("/doctors", payload);
      else await api.patch(`/doctors/${editing.id}`, payload);
      toast.success(editing === "new" ? "Doctor added" : "Doctor updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete doctor?")) return;
    try { await api.delete(`/doctors/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-3 space-y-3">
      <div className="flex justify-end">
        {canManage && (
          <Button onClick={openCreate} data-testid="doc-add" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
            <Plus size={14} weight="bold" className="mr-1.5" /> New doctor
          </Button>
        )}
      </div>
      {list.length === 0 ? <EmptyState title="No doctors yet" hint="Add doctors to make them appear in IPD/OPD dropdowns." /> : (
        <SimpleTable
          rows={list}
          columns={[
            { key: "name", label: "Name", className: "font-medium" },
            { key: "specialization", label: "Specialization" },
            { key: "department", label: "Department" },
            { key: "phone", label: "Phone" },
            { key: "consultation_fee", label: "Fee", render: (v) => `₹${Number(v || 0).toFixed(2)}`, className: "hms-tabular" },
          ]}
          onEdit={openEdit}
          onDelete={del}
          testidPrefix="doc-row"
        />
      )}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "New doctor" : `Edit · ${editing?.name}`}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="doc-form">
            <F label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="doc-name" colSpan={2} />
            <F label="Qualification" value={form.qualification} onChange={(v) => setForm({ ...form, qualification: v })} testid="doc-qual" />
            <F label="Specialization" value={form.specialization} onChange={(v) => setForm({ ...form, specialization: v })} testid="doc-spec" />
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Department</Label>
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger data-testid="doc-dept" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {depts.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <F label="Reg. No." value={form.registration_no} onChange={(v) => setForm({ ...form, registration_no: v })} testid="doc-reg" />
            <F label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="doc-phone" />
            <F label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="doc-email" />
            <F label="Consult fee" type="number" value={form.consultation_fee} onChange={(v) => setForm({ ...form, consultation_fee: v })} testid="doc-fee" />
            <DialogFooter className="col-span-2"><Button type="submit" data-testid="doc-submit" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">{editing === "new" ? "Save" : "Update"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============== WARDS & BEDS ============== */
const WARD_BLANK = { name: "", type: "General", floor: "", notes: "" };
function WardsTab() {
  const canManage = useCanManage();
  const [wards, setWards] = useState([]);
  const [beds, setBeds] = useState([]);
  const [activeWard, setActiveWard] = useState(null);
  const [editingWard, setEditingWard] = useState(null);
  const [editingBed, setEditingBed] = useState(null);
  const [wForm, setWForm] = useState(WARD_BLANK);
  const [bForm, setBForm] = useState({ ward_id: "", bed_no: "", daily_rate: 0, notes: "" });

  const loadWards = useCallback(() => api.get("/wards").then((r) => setWards(r.data || [])), []);
  const loadBeds = useCallback(() => api.get("/beds").then((r) => setBeds(r.data || [])), []);
  useEffect(() => { loadWards(); loadBeds(); }, [loadWards, loadBeds]);

  const openCreateWard = () => { setWForm(WARD_BLANK); setEditingWard("new"); };
  const openEditWard = (w) => {
    setWForm({ name: w.name || "", type: w.type || "General", floor: w.floor || "", notes: w.notes || "" });
    setEditingWard(w);
  };
  const submitWard = async (e) => {
    e.preventDefault();
    try {
      if (editingWard === "new") await api.post("/wards", wForm);
      else await api.patch(`/wards/${editingWard.id}`, wForm);
      toast.success(editingWard === "new" ? "Ward added" : "Ward updated");
      setEditingWard(null); loadWards();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delWard = async (id) => {
    if (!window.confirm("Delete ward?")) return;
    try { await api.delete(`/wards/${id}`); toast.success("Removed"); loadWards(); loadBeds(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openCreateBed = () => {
    setBForm({ ward_id: activeWard || "", bed_no: "", daily_rate: 0, notes: "" });
    setEditingBed("new");
  };
  const openEditBed = (b) => {
    setBForm({ ward_id: b.ward_id || "", bed_no: b.bed_no || "", daily_rate: b.daily_rate || 0, notes: b.notes || "" });
    setEditingBed(b);
  };
  const submitBed = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...bForm, daily_rate: Number(bForm.daily_rate || 0) };
      if (editingBed === "new") await api.post("/beds", payload);
      else await api.patch(`/beds/${editingBed.id}`, payload);
      toast.success(editingBed === "new" ? "Bed added" : "Bed updated");
      setEditingBed(null); loadBeds();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delBed = async (id) => {
    if (!window.confirm("Delete bed?")) return;
    try { await api.delete(`/beds/${id}`); toast.success("Removed"); loadBeds(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filteredBeds = activeWard ? beds.filter((b) => b.ward_id === activeWard) : beds;
  const bedsPg = usePagination(filteredBeds, 50);

  return (
    <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Wards */}
      <div className="hms-card p-4 lg:col-span-1">
        <div className="flex items-center justify-between mb-3">
          <div className="hms-section-title">Wards</div>
          <Button size="sm" onClick={openCreateWard} data-testid="ward-add" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]"><Plus size={12} weight="bold" /></Button>
        </div>
        {wards.length === 0 ? <div className="text-xs text-[var(--hms-muted)]">No wards yet.</div> : (
          <ul className="space-y-1">
            <li>
              <button onClick={() => setActiveWard(null)} className={`w-full text-left px-2 py-1.5 text-sm rounded-sm ${!activeWard ? "bg-[var(--hms-paper)] font-semibold" : "hover:bg-[var(--hms-paper)]/50"}`} data-testid="ward-filter-all">All beds</button>
            </li>
            {wards.map((w) => (
              <li key={w.id} className="flex items-center group">
                <button onClick={() => setActiveWard(w.id)} className={`flex-1 text-left px-2 py-1.5 text-sm rounded-sm ${activeWard === w.id ? "bg-[var(--hms-paper)] font-semibold" : "hover:bg-[var(--hms-paper)]/50"}`} data-testid={`ward-pick-${w.name}`}>
                  <span>{w.name}</span>
                  <span className="text-[10px] text-[var(--hms-muted)] ml-2 uppercase">{w.type}</span>
                </button>
                <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100" onClick={() => openEditWard(w)} data-testid={`ward-edit-${w.name}`}>
                  <PencilSimple size={12} weight="bold" />
                </Button>
                {canManage && (
                  <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100 text-[var(--hms-accent)]" onClick={() => delWard(w.id)} data-testid={`ward-del-${w.name}`}><Trash size={12} weight="bold" /></Button>
                )}
              </li>
            ))}
          </ul>
        )}
        <Dialog open={!!editingWard} onOpenChange={(v) => !v && setEditingWard(null)}>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle className="font-heading">{editingWard === "new" ? "New ward" : `Edit · ${editingWard?.name}`}</DialogTitle></DialogHeader>
            <form onSubmit={submitWard} className="grid grid-cols-2 gap-3" data-testid="ward-form">
              <F label="Name" required value={wForm.name} onChange={(v) => setWForm({ ...wForm, name: v })} testid="ward-name" colSpan={2} />
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Type</Label>
                <Select value={wForm.type} onValueChange={(v) => setWForm({ ...wForm, type: v })}>
                  <SelectTrigger data-testid="ward-type" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["General", "Semi-Private", "Private", "Deluxe", "ICU", "ICCU", "NICU", "PICU", "Maternity", "Day-Care"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <F label="Floor" value={wForm.floor} onChange={(v) => setWForm({ ...wForm, floor: v })} testid="ward-floor" />
              <DialogFooter className="col-span-2"><Button type="submit" data-testid="ward-submit" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">{editingWard === "new" ? "Save" : "Update"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Beds */}
      <div className="hms-card p-4 lg:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <div className="hms-section-title">Beds {activeWard && <span className="ml-2 text-[10px] text-[var(--hms-muted)]">(filtered to ward)</span>}</div>
          <Button size="sm" onClick={openCreateBed} data-testid="bed-add" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]" disabled={wards.length === 0}>
            <Plus size={12} weight="bold" className="mr-1" /> New bed
          </Button>
        </div>
        {filteredBeds.length === 0 ? <div className="text-xs text-[var(--hms-muted)]">No beds yet.</div> : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[var(--hms-paper)] text-[10px] uppercase tracking-wider font-bold text-[var(--hms-text-2)] border-b border-[var(--hms-border-2)]">
                <tr>
                  <th className="text-left px-3 py-2">Bed No.</th>
                  <th className="text-left px-3 py-2">Ward</th>
                  <th className="text-right px-3 py-2">Daily rate</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {bedsPg.paged.map((b) => {
                  const ward = wards.find((w) => w.id === b.ward_id);
                  return (
                    <tr key={b.id} className="border-b border-[var(--hms-border)]" data-testid={`bed-row-${b.bed_no}`}>
                      <td className="px-3 py-2 font-mono text-xs">{b.bed_no}</td>
                      <td className="px-3 py-2">{ward?.name || "—"}</td>
                      <td className="px-3 py-2 text-right hms-tabular">₹{Number(b.daily_rate || 0).toFixed(2)}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest border ${b.status === "occupied" ? "bg-[#C8553D]/15 text-[#C8553D] border-[#C8553D]/30" : "bg-[#588157]/15 text-[#588157] border-[#588157]/30"}`}>
                          {b.status === "occupied" ? "Occupied" : "Available"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {canManage && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => openEditBed(b)} className="rounded-sm" data-testid={`bed-edit-${b.bed_no}`}><PencilSimple size={12} weight="bold" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => delBed(b.id)} className="text-[var(--hms-accent)] rounded-sm" data-testid={`bed-del-${b.bed_no}`}><Trash size={12} weight="bold" /></Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination page={bedsPg.page} setPage={bedsPg.setPage}
              pageSize={bedsPg.pageSize} setPageSize={bedsPg.setPageSize}
              total={bedsPg.total} pageCount={bedsPg.pageCount} testidPrefix="bed-row" />
          </div>
        )}
        <Dialog open={!!editingBed} onOpenChange={(v) => !v && setEditingBed(null)}>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle className="font-heading">{editingBed === "new" ? "New bed" : `Edit · ${editingBed?.bed_no}`}</DialogTitle></DialogHeader>
            <form onSubmit={submitBed} className="grid grid-cols-2 gap-3" data-testid="bed-form">
              <div className="col-span-2">
                <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Ward *</Label>
                <Select value={bForm.ward_id} onValueChange={(v) => setBForm({ ...bForm, ward_id: v })}>
                  <SelectTrigger data-testid="bed-ward" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue placeholder="Select ward" /></SelectTrigger>
                  <SelectContent>{wards.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <F label="Bed No." required value={bForm.bed_no} onChange={(v) => setBForm({ ...bForm, bed_no: v })} testid="bed-no" />
              <F label="Daily rate" type="number" value={bForm.daily_rate} onChange={(v) => setBForm({ ...bForm, daily_rate: v })} testid="bed-rate" />
              <DialogFooter className="col-span-2"><Button type="submit" data-testid="bed-submit" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">{editingBed === "new" ? "Save" : "Update"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

/* ============== CHARGES ============== */
const CHARGE_BLANK = { code: "", name: "", category: "Service", rate: 0, taxable: false, is_default: false, notes: "" };
function ChargesTab() {
  const canManage = useCanManage();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(CHARGE_BLANK);
  const load = useCallback(() => api.get("/charges").then((r) => setList(r.data || [])), []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(CHARGE_BLANK); setEditing("new"); };
  const openEdit = (row) => {
    setForm({
      code: row.code || "", name: row.name || "", category: row.category || "Service",
      rate: row.rate || 0, taxable: !!row.taxable, is_default: !!row.is_default, notes: row.notes || "",
    });
    setEditing(row);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, rate: Number(form.rate || 0) };
      if (editing === "new") await api.post("/charges", payload);
      else await api.patch(`/charges/${editing.id}`, payload);
      toast.success(editing === "new" ? "Charge added" : "Charge updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete charge?")) return;
    try { await api.delete(`/charges/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-3 space-y-3">
      <div className="flex justify-end">
        {canManage && (
          <Button onClick={openCreate} data-testid="charge-add" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
            <Plus size={14} weight="bold" className="mr-1.5" /> New charge
          </Button>
        )}
      </div>
      {list.length === 0 ? <EmptyState title="No charges yet" hint="Add a charge — those marked Default will auto-load on every new bill." /> : (
        <SimpleTable
          rows={list}
          columns={[
            { key: "code", label: "Code", mono: true },
            { key: "name", label: "Name", className: "font-medium" },
            { key: "category", label: "Category" },
            { key: "rate", label: "Rate", render: (v) => `₹${Number(v || 0).toFixed(2)}`, className: "hms-tabular text-right" },
            { key: "is_default", label: "Default", render: (v) => v ? "Yes" : "—" },
          ]}
          onEdit={openEdit}
          onDelete={del}
          testidPrefix="charge-row"
        />
      )}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "New charge" : `Edit · ${editing?.name}`}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="charge-form">
            <F label="Code" required value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} testid="charge-code" />
            <F label="Rate" type="number" value={form.rate} onChange={(v) => setForm({ ...form, rate: v })} testid="charge-rate" />
            <F label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="charge-name" colSpan={2} />
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="charge-cat" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Room", "Doctor", "Nursing", "Pharmacy", "Lab", "Procedure", "Service", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex items-center gap-2"><Switch checked={form.taxable} onCheckedChange={(v) => setForm({ ...form, taxable: v })} data-testid="charge-tax" /><Label className="text-xs">Taxable</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} data-testid="charge-default" /><Label className="text-xs">Default in bill</Label></div>
            </div>
            <DialogFooter className="col-span-2"><Button type="submit" data-testid="charge-submit" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">{editing === "new" ? "Save" : "Update"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============== MEDICINES (with 3 sub-tabs) ============== */
const MED_TABS = [
  { key: "medicine", label: "Medicines" },
  { key: "category", label: "Categories" },
  { key: "schedule", label: "When to take" },
];

function MedicinesTab() {
  const [sub, setSub] = useState("medicine");
  return (
    <div className="mt-3 space-y-3">
      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="bg-white rounded-sm border border-[var(--hms-border-2)]">
          {MED_TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`med-sub-${t.key}`} className="rounded-sm data-[state=active]:bg-[var(--hms-paper)]">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="medicine"><MedicineRows /></TabsContent>
        <TabsContent value="category"><MedicineCategoryRows /></TabsContent>
        <TabsContent value="schedule"><MedicineScheduleRows /></TabsContent>
      </Tabs>
    </div>
  );
}

const MED_BLANK = { name: "", category: "", generic_name: "", notes: "" };
function MedicineRows() {
  const canManage = useCanManage();
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(MED_BLANK);
  const load = useCallback(() => api.get("/medicines").then((r) => setList(r.data || [])), []);
  useEffect(() => { load(); api.get("/medicine-categories").then((r) => setCats(r.data || [])); }, [load]);

  const openCreate = () => { setForm(MED_BLANK); setEditing("new"); };
  const openEdit = (r) => {
    setForm({ name: r.name || "", category: r.category || "", generic_name: r.generic_name || "", notes: r.notes || "" });
    setEditing(r);
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/medicines", form);
      else await api.patch(`/medicines/${editing.id}`, form);
      toast.success(editing === "new" ? "Medicine added" : "Medicine updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete medicine?")) return;
    try { await api.delete(`/medicines/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-3 space-y-3">
      <div className="flex justify-end">
        {canManage && (
          <Button onClick={openCreate} data-testid="medicine-add" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
            <Plus size={14} weight="bold" className="mr-1.5" /> New medicine
          </Button>
        )}
      </div>
      {list.length === 0 ? <EmptyState title="No medicines yet" hint="Add medicines you stock and prescribe." /> : (
        <SimpleTable
          rows={list}
          columns={[
            { key: "name", label: "Name", className: "font-medium" },
            { key: "category", label: "Form / Category" },
            { key: "generic_name", label: "Generic" },
            { key: "notes", label: "Notes" },
          ]}
          onEdit={openEdit} onDelete={del} testidPrefix="medicine-row"
        />
      )}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "New medicine" : `Edit · ${editing?.name}`}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="medicine-form">
            <F label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="medicine-name" colSpan={2} />
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Form / Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="medicine-cat" className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"><SelectValue placeholder={cats.length ? "Select…" : "Add categories first"} /></SelectTrigger>
                <SelectContent>
                  {cats.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <F label="Generic name" value={form.generic_name} onChange={(v) => setForm({ ...form, generic_name: v })} testid="medicine-generic" />
            <FT label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="medicine-notes" />
            <DialogFooter className="col-span-2"><Button type="submit" data-testid="medicine-submit" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">{editing === "new" ? "Save" : "Update"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MEDCAT_BLANK = { name: "", notes: "" };
function MedicineCategoryRows() {
  const canManage = useCanManage();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(MEDCAT_BLANK);
  const load = useCallback(() => api.get("/medicine-categories").then((r) => setList(r.data || [])), []);
  useEffect(() => { load(); }, [load]);
  const openCreate = () => { setForm(MEDCAT_BLANK); setEditing("new"); };
  const openEdit = (r) => { setForm({ name: r.name || "", notes: r.notes || "" }); setEditing(r); };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/medicine-categories", form);
      else await api.patch(`/medicine-categories/${editing.id}`, form);
      toast.success(editing === "new" ? "Category added" : "Category updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete category? (Medicines tagged with it will keep the name as a free-text label.)")) return;
    try { await api.delete(`/medicine-categories/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="mt-3 space-y-3">
      <div className="flex justify-end">
        {canManage && (
          <Button onClick={openCreate} data-testid="medcat-add" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
            <Plus size={14} weight="bold" className="mr-1.5" /> New category
          </Button>
        )}
      </div>
      {list.length === 0 ? <EmptyState title="No categories yet" hint='Add forms like "Tablet", "Capsule", "Syrup", "Injection", "Cream".' /> : (
        <SimpleTable
          rows={list}
          columns={[
            { key: "name", label: "Name", className: "font-medium" },
            { key: "notes", label: "Notes" },
          ]}
          onEdit={openEdit} onDelete={del} testidPrefix="medcat-row"
        />
      )}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "New medicine category" : `Edit · ${editing?.name}`}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="medcat-form">
            <F label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="medcat-name" colSpan={2} />
            <FT label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="medcat-notes" />
            <DialogFooter className="col-span-2"><Button type="submit" data-testid="medcat-submit" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">{editing === "new" ? "Save" : "Update"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MEDSCH_BLANK = { label: "", notes: "" };
function MedicineScheduleRows() {
  const canManage = useCanManage();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(MEDSCH_BLANK);
  const load = useCallback(() => api.get("/medicine-schedules").then((r) => setList(r.data || [])), []);
  useEffect(() => { load(); }, [load]);
  const openCreate = () => { setForm(MEDSCH_BLANK); setEditing("new"); };
  const openEdit = (r) => { setForm({ label: r.label || "", notes: r.notes || "" }); setEditing(r); };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/medicine-schedules", form);
      else await api.patch(`/medicine-schedules/${editing.id}`, form);
      toast.success(editing === "new" ? "Schedule added" : "Schedule updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this 'when to take' option?")) return;
    try { await api.delete(`/medicine-schedules/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="mt-3 space-y-3">
      <div className="flex justify-end">
        {canManage && (
          <Button onClick={openCreate} data-testid="medsch-add" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
            <Plus size={14} weight="bold" className="mr-1.5" /> New schedule
          </Button>
        )}
      </div>
      {list.length === 0 ? <EmptyState title="No schedules yet" hint='Add timings like "1-0-1", "0-0-1", "After food", "Before sleep", "SOS".' /> : (
        <SimpleTable
          rows={list}
          columns={[
            { key: "label", label: "Label", className: "font-medium font-mono" },
            { key: "notes", label: "Notes" },
          ]}
          onEdit={openEdit} onDelete={del} testidPrefix="medsch-row"
        />
      )}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "New 'when to take' option" : `Edit · ${editing?.label}`}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="medsch-form">
            <F label="Label" required value={form.label} onChange={(v) => setForm({ ...form, label: v })} testid="medsch-label" colSpan={2} />
            <FT label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="medsch-notes" />
            <DialogFooter className="col-span-2"><Button type="submit" data-testid="medsch-submit" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">{editing === "new" ? "Save" : "Update"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============== SHARED ============== */
function F({ label, value, onChange, required, type, testid, colSpan }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">{label}{required && " *"}</Label>
      <Input type={type || "text"} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
    </div>
  );
}
function FT({ label, value, onChange, testid }) {
  return (
    <div className="col-span-2">
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">{label}</Label>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
    </div>
  );
}
function SimpleTable({ rows, columns, onEdit, onDelete, testidPrefix }) {
  const canManage = useCanManage();
  const showActions = canManage && !!(onEdit || onDelete);
  const { page, setPage, pageSize, setPageSize, paged, total, pageCount } = usePagination(rows, 50);
  return (
    <div className="hms-card overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-[var(--hms-paper)] text-[10px] uppercase tracking-wider font-bold text-[var(--hms-text-2)] border-b border-[var(--hms-border-2)]">
          <tr>
            {columns.map((c) => <th key={c.key} className="text-left px-4 py-2.5">{c.label}</th>)}
            {showActions && <th className="px-4 py-2.5 text-right">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {paged.map((r) => (
            <tr key={r.id} className="border-b border-[var(--hms-border)] hover:bg-[var(--hms-paper)]/40" data-testid={`${testidPrefix}-${r.id}`}>
              {columns.map((c) => {
                const v = r[c.key];
                return (
                  <td key={c.key} className={`px-4 py-2.5 ${c.mono ? "font-mono text-xs" : ""} ${c.className || ""}`}>
                    {c.render ? c.render(v) : (v ?? "—")}
                  </td>
                );
              })}
              {showActions && (
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  {onEdit && (
                    <Button size="sm" variant="ghost" onClick={() => onEdit(r)} className="rounded-sm" data-testid={`${testidPrefix}-edit-${r.id}`}>
                      <PencilSimple size={14} weight="bold" />
                    </Button>
                  )}
                  {onDelete && (
                    <Button size="sm" variant="ghost" onClick={() => onDelete(r.id)} className="text-[var(--hms-accent)] rounded-sm" data-testid={`${testidPrefix}-del-${r.id}`}>
                      <Trash size={14} weight="bold" />
                    </Button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination page={page} setPage={setPage} pageSize={pageSize}
        setPageSize={setPageSize} total={total} pageCount={pageCount} testidPrefix={testidPrefix} />
    </div>
  );
}
